import { useState } from "react";
import { Button, IconProvider } from "../ui";
import { addInterestedUser } from "@/lib/api-functions/api-interested-users";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/lib/config/supabase";

import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
interface SubscribeStep1Props {
  onNext: (email: string) => void;
}

const SubscribeStep1: React.FC<SubscribeStep1Props> = ({ onNext }) => {
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isWorkEmail = (email: string): boolean => {
    const personalDomains = [
      "gmail.com",
      "yahoo.com",
      "hotmail.com",
      "outlook.com",
      "aol.com",
      "icloud.com",
      "me.com",
      "live.com",
      "msn.com",
      "ymail.com",
      "rocketmail.com",
      "mail.com",
      "gmx.com",
      "zoho.com",
      "protonmail.com",
    ];

    const emailDomain = email.toLowerCase().split("@")[1];
    return !personalDomains.includes(emailDomain);
  };

  const validateEmail = (email: string): string => {
    if (!email) {
      return "Email is required";
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return "Please enter a valid email address";
    }

    if (!isWorkEmail(email)) {
      return "Please use a work email address (personal email providers like Gmail, Yahoo, etc. are not allowed)";
    }

    return "";
  };

  const handleSubmit = (e: React.FormEvent) => {
  const { t } = usePlatformTranslation();
    e.preventDefault();

    const error = validateEmail(email);
    setEmailError(error);

    if (!error) {
      onNext(email);
    }
  };

  const handleKeepMeUpToDate = async (e: React.FormEvent) => {
    e.preventDefault();

    const error = validateEmail(email);
    setEmailError(error);

    if (error) {
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await addInterestedUser({ email });

      if (response.success) {
        toast({
          title: "Success!",
          description: "You've been added to our interested users list. We'll keep you updated on LocaNest developments!",
        });
        // Clear the email field after successful submission
        setEmail("");
      } else {
        toast({
          title: "Submission Failed",
          description: response.message || "Please try again later.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error adding interested user:", error);
      toast({
        title: "Submission Failed",
        description: "An unexpected error occurred. Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          },
        },
      });

      if (error) {
        console.error('Google OAuth error:', error);
        toast({
          title: "Login Failed",
          description: "Failed to initiate Google login. Please try again.",
          variant: "destructive",
        });
      } else {
        // The user will be redirected to Google's OAuth page
        console.log('Google OAuth initiated successfully');
      }
    } catch (error) {
      console.error('Unexpected error during Google login:', error);
      toast({
        title: "Login Failed",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleDiscordLogin = async () => {
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'discord',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
        },
      });

      if (error) {
        console.error('Discord OAuth error:', error);
        toast({
          title: "Login Failed",
          description: "Failed to initiate Discord login. Please try again.",
          variant: "destructive",
        });
      } else {
        // The user will be redirected to Discord's OAuth page
        console.log('Discord OAuth initiated successfully');
      }
    } catch (error) {
      console.error('Unexpected error during Discord login:', error);
      toast({
        title: "Login Failed",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <>
      <div>
        <p className="text-left text-gray-900 mb-4 text-sm">
          Please fill out the form below to request access to LocaNest.
          <br />
          We are currently in closed Beta and are looking for feedback from our
          users.
          <br />
          <br />
          At this moment we are taking in <strong>50 users at a time</strong>.
          <br />
          <br />
        This way we can make sure we are building the right product for you. 
          <br />
          Because no game is the same, so listening to <strong>your feedback is crucial </strong>.
        </p>
      </div>

      {/* Google Login Button */}
     {/*  <button
        type="button"
        onClick={handleGoogleLogin}
        className="w-full h-10 px-4 py-2 bg-white border border-gray-300 rounded-sm text-gray-700 font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-1 focus:border-transparent flex items-center justify-center gap-2 mb-4"
      >
        <IconProvider iconstring="fa-google" className="w-4 h-4" />
        Continue with Google
      </button>
      <button
        type="button"
        onClick={handleDiscordLogin}
        className="w-full h-10 px-4 py-2 bg-white border border-gray-300 rounded-sm text-gray-700 font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-1 focus:border-transparent flex items-center justify-center gap-2 mb-4"
      >
        <IconProvider iconstring="fa-discord" className="w-4 h-4" />
        Continue with Discord
      </button>

      <div className="relative mb-4">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-300" />
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-2 bg-white text-gray-500">or</span>
        </div>
      </div> */}

      <div className="mb-4">
        <input
          type="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (emailError) {
              setEmailError("");
            }
          }}
          onBlur={() => {
            if (email) {
              const error = validateEmail(email);
              setEmailError(error);
            }
          }}
          placeholder={t('platform.common.email')}
          required
          className={`w-full h-8 px-4 py-3 border rounded-sm focus:outline-none focus:ring-2 focus:border-transparent ${
            emailError
              ? "border-red-500 focus:ring-red-500"
              : "border-gray-300 focus:ring-purple-1"
          }`}
        />
        {emailError && (
          <p className="text-red-500 text-xs mt-1">{emailError}</p>
        )}
      </div>
      <div className="flex flex-col sm:flex-row gap-2 w-full">
        <Button
          type="submit"
          variant="outline"
          className="w-full"
          onClick={handleKeepMeUpToDate}
          disabled={isSubmitting}
        >
          {isSubmitting ? "Adding..." : "Keep me up to date"}
        </Button>
        <Button
          type="submit"
          variant="default"
          className="w-full"
          onClick={handleSubmit}
        >
          Join the Beta!
        </Button>
      </div>
    </>
  );
};

export default SubscribeStep1;
